import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";

interface Props {
  /** Translation key prefix for the help block, e.g. "credentials.help". */
  i18nKey: string;
  /** Number of paragraphs to render — uses keys `${i18nKey}.p1`, `.p2`, ... */
  paragraphs: number;
  /** Optional list section — uses `${i18nKey}.list_title` + items. */
  list?: { titleKey: string; itemsKey: string; itemCount: number };
  /** Renders the section open by default. Closed by default keeps the page scannable. */
  defaultOpen?: boolean;
  className?: string;
  /** Extra content (rich JSX) appended after paragraphs. */
  children?: ReactNode;
}

/**
 * In-page help drawer — a friendlier alternative to docs the user has to
 * leave the app for. Each page mounts one of these at the bottom; collapsed
 * by default so it doesn't fight with the data table for attention.
 */
export function HelpSection({
  i18nKey,
  paragraphs,
  list,
  defaultOpen = false,
  className,
  children,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <details
      className={cn(
        "rounded-lg border border-border bg-bg-secondary/50",
        "[&_summary::-webkit-details-marker]:hidden",
        className,
      )}
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer px-4 py-3 list-none flex items-center gap-2 text-sm font-medium text-fg-secondary hover:text-fg-primary">
        {open ? (
          <ChevronDown aria-hidden="true" size={16} strokeWidth={2} />
        ) : (
          <ChevronRight aria-hidden="true" size={16} strokeWidth={2} />
        )}
        <span>{t(`${i18nKey}.title`)}</span>
      </summary>
      <div className="px-4 pb-4 space-y-3 text-sm text-fg-secondary leading-relaxed">
        {Array.from({ length: paragraphs }, (_, i) => (
          <p key={i}>{t(`${i18nKey}.p${i + 1}`)}</p>
        ))}
        {list && (
          <div className="space-y-1.5">
            <p className="text-fg-primary font-medium">{t(list.titleKey)}</p>
            <ul className="space-y-1 list-disc pl-5">
              {Array.from({ length: list.itemCount }, (_, i) => (
                <li key={i}>{t(`${list.itemsKey}.${i + 1}`)}</li>
              ))}
            </ul>
          </div>
        )}
        {children}
      </div>
    </details>
  );
}
